// ═══════════════════════════════════════════
// Cash Closing — denomination-wise end-of-shift reconciliation
// Traceability: FR-BILL-048, FR-BILL-030, PAGES.md §2.31, §3.100
// ═══════════════════════════════════════════

import type { CurrencyCode } from './common';

/** Map of denomination face value (as string) → count. */
export type Denominations = Record<string, number>;

// ─── Denomination ladders ───────────────────────────────────────────────────
//
// The cash a drawer can physically hold, per currency. Single source of truth
// for the POS count pages, the shared DenominationEntry component, the admin
// history drill-down and the API's `actual_cash` computation — each of which
// previously carried its own hardcoded Indian ladder.
//
// Faces are MINOR units (paise, baisa, fils) and always integers. That keeps
// `actual_cash` exact integer arithmetic: a major-unit ladder would need
// `0.005 * 1000`, and JS gives 5.000000000000001 for that.

/** One note or coin a drawer can hold. */
export interface DenominationFace {
    /** Face value in MINOR units — an exact integer (OMR 0.5 rial → 500 baisa). */
    readonly minor: number;
    readonly type: 'note' | 'coin';
}

/**
 * Per-currency ladders, ordered NOTES first then COINS, descending within each
 * group. That order is load-bearing: the POS count page splits the list with
 * `startIndex={notes.length}` and looks face metadata up by global index.
 *
 * The six GCC ladders were drafted from general knowledge rather than an
 * authoritative central-bank source, and were reviewed and signed off by the
 * business on 2026-09-09. The INR ladder is unchanged and in production use.
 *
 * Changing a face here changes what a cashier counts at close, so treat any
 * edit as a business decision, not a code one.
 */
export const DENOMINATIONS_BY_CURRENCY: Readonly<
    Record<CurrencyCode, readonly DenominationFace[]>
> = Object.freeze({
    // ₹2000/500/200/100/50/20 notes; ₹10/5/2/1 coins. In production — do not change.
    INR: Object.freeze([
        { minor: 200000, type: 'note' }, { minor: 50000, type: 'note' },
        { minor: 20000,  type: 'note' }, { minor: 10000, type: 'note' },
        { minor: 5000,   type: 'note' }, { minor: 2000,  type: 'note' },
        { minor: 1000,   type: 'coin' }, { minor: 500,   type: 'coin' },
        { minor: 200,    type: 'coin' }, { minor: 100,   type: 'coin' },
    ] as const),
    // 1000/500/200/100/50/20/10/5 dirham notes; 1 dirham, 50 and 25 fils coins.
    AED: Object.freeze([
        { minor: 100000, type: 'note' }, { minor: 50000, type: 'note' },
        { minor: 20000,  type: 'note' }, { minor: 10000, type: 'note' },
        { minor: 5000,   type: 'note' }, { minor: 2000,  type: 'note' },
        { minor: 1000,   type: 'note' }, { minor: 500,   type: 'note' },
        { minor: 100,    type: 'coin' }, { minor: 50,    type: 'coin' },
        { minor: 25,     type: 'coin' },
    ] as const),
    // 500/100/50/10/5 riyal notes, plus the 200 and 20 commemoratives that do
    // circulate; 2 and 1 riyal, 50 and 25 halala coins. The 1 riyal note was
    // withdrawn, so 1 is listed as a coin (it was previously a note here).
    SAR: Object.freeze([
        { minor: 50000, type: 'note' }, { minor: 20000, type: 'note' },
        { minor: 10000, type: 'note' }, { minor: 5000,  type: 'note' },
        { minor: 2000,  type: 'note' }, { minor: 1000,  type: 'note' },
        { minor: 500,   type: 'note' },
        { minor: 200,   type: 'coin' }, { minor: 100,   type: 'coin' },
        { minor: 50,    type: 'coin' }, { minor: 25,    type: 'coin' },
    ] as const),
    // 500/100/50/10/5/1 riyal notes; 50 and 25 dirham coins.
    QAR: Object.freeze([
        { minor: 50000, type: 'note' }, { minor: 10000, type: 'note' },
        { minor: 5000,  type: 'note' }, { minor: 1000,  type: 'note' },
        { minor: 500,   type: 'note' }, { minor: 100,   type: 'note' },
        { minor: 50,    type: 'coin' }, { minor: 25,    type: 'coin' },
    ] as const),
    // 50/20/10/5/1 rial and 500/100 baisa notes; 50/25/10/5 baisa coins.
    OMR: Object.freeze([
        { minor: 50000, type: 'note' }, { minor: 20000, type: 'note' },
        { minor: 10000, type: 'note' }, { minor: 5000,  type: 'note' },
        { minor: 1000,  type: 'note' }, { minor: 500,   type: 'note' },
        { minor: 100,   type: 'note' },
        { minor: 50,    type: 'coin' }, { minor: 25,    type: 'coin' },
        { minor: 10,    type: 'coin' }, { minor: 5,     type: 'coin' },
    ] as const),
    // 20/10/5/1 dinar and 500 fils notes; 100/50/25/10/5 fils coins.
    BHD: Object.freeze([
        { minor: 20000, type: 'note' }, { minor: 10000, type: 'note' },
        { minor: 5000,  type: 'note' }, { minor: 1000,  type: 'note' },
        { minor: 500,   type: 'note' },
        { minor: 100,   type: 'coin' }, { minor: 50,    type: 'coin' },
        { minor: 25,    type: 'coin' }, { minor: 10,    type: 'coin' },
        { minor: 5,     type: 'coin' },
    ] as const),
    // 20/10/5/1 dinar and 500/250 fils notes; 100/50/20/10/5 fils coins.
    KWD: Object.freeze([
        { minor: 20000, type: 'note' }, { minor: 10000, type: 'note' },
        { minor: 5000,  type: 'note' }, { minor: 1000,  type: 'note' },
        { minor: 500,   type: 'note' }, { minor: 250,   type: 'note' },
        { minor: 100,   type: 'coin' }, { minor: 50,    type: 'coin' },
        { minor: 20,    type: 'coin' }, { minor: 10,    type: 'coin' },
        { minor: 5,     type: 'coin' },
    ] as const),
});

/** The ladder for a currency. Falls back to INR for an unknown code. */
export function getDenominationFaces(currency: CurrencyCode): readonly DenominationFace[] {
    return DENOMINATIONS_BY_CURRENCY[currency] ?? DENOMINATIONS_BY_CURRENCY.INR;
}

/**
 * The `denominations` JSONB key for a face: its MAJOR-unit value as a string
 * (₹500 → "500", 500 baisa → "0.5").
 *
 * Major units, not minor, because `cash_closings` has no currency column — every
 * historical INR row would silently reinterpret "500" from ₹500 to ₹5. The
 * subunit divisor is a parameter rather than a lookup so this package stays free
 * of a runtime dependency on `@bbpos/utils`; callers pass
 * `getSubunitMultiplier(currency)`.
 */
export function denominationKey(minorFace: number, subunit: number): string {
    return String(minorFace / subunit);
}

export type CashClosingStatus = 'CLOSED' | 'REVIEWED';

/**
 * Expected-cash preview computed from the current attendance session.
 * Returned by `GET /cash-closing/preview?attendance_id=...`.
 */
export interface CashClosingPreview {
    attendance_id: string;
    location_id: string;
    shift_id: string | null;
    /** Paise — opening cash float recorded at clock-in. */
    opening_float: number;
    /** Paise — SUM of CASH bill payments by this cashier in this session. */
    cash_sales: number;
    /** Paise — SUM of approved CASH expenses at this location in this session. */
    cash_expenses: number;
    /** Paise — cash paid out of the drawer by CASH returns in this session. */
    cash_refunds: number;
    /** Paise — opening_float + cash_sales - cash_refunds - cash_expenses. */
    expected_cash: number;
    /** Tenant-configured variance threshold (percent). Defaults to 2. */
    variance_threshold_pct: number;
    /** True when a cash closing already exists for this attendance. */
    already_closed: boolean;
    /** Id of the existing closing when `already_closed`, else null. */
    existing_closing_id: string | null;
    computed_at: string;
}

/**
 * Payload accepted by `POST /cash-closing`.
 * Shared by POS and Admin clients.
 */
export interface CreateCashClosingPayload {
    attendance_id: string;
    location_id: string;
    shift_id?: string;
    denominations: Denominations;
    /** Max 500 chars. Required when variance exceeds threshold (client-enforced). */
    notes?: string;
}

/**
 * A single cash closing row — returned by both the create endpoint and
 * `GET /cash-closing` list endpoint.
 */
export interface CashClosingRecord {
    id: string;
    location_id: string;
    location_name: string;
    attendance_id: string | null;
    shift_id: string | null;
    /** Name of the shift the closing belongs to (null when it has none). */
    shift_name: string | null;
    /** Paise. */
    actual_cash: number;
    /** Paise. */
    expected_cash: number;
    /** Paise. actual_cash - expected_cash. */
    variance: number;
    /** Absolute variance percent. */
    variance_pct: number;
    denominations: Denominations;
    status: CashClosingStatus;
    notes: string | null;
    closed_by: string;
    closed_by_name: string;
    closed_at: string;
}

/**
 * List response envelope.
 * Server shape: `{ data: CashClosingRecord[], meta: { page, limit, total, totalPages } }`.
 */
export interface CashClosingListResponse {
    data: CashClosingRecord[];
    meta: { page: number; limit: number; total: number; totalPages: number };
}
